import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import toastr from "toastr";
import { MaterialReactTable } from "material-react-table";

const API = process.env.REACT_APP_API_BASE_URL;

const STATUS_OPTIONS = ["pending", "paid", "not-paid", "hold"];

/**
 * FINANCE screen (department_id = 4).
 * Replaces the old ReimbursementApproval.jsx — there is no manager approval
 * stage, because "Authorised By" on the submission form already records an
 * approval that happened offline. Finance only moves the status.
 */
export default function ReimbursementPayments() {
  const { setGlobalLoading, token } = useOutletContext();

  const [access, setAccess] = useState({
    canChangeStatus: false,
    isFinance: false,
    loaded: false,
  });
  const [statusTab, setStatusTab] = useState("pending");
  const [rows, setRows] = useState([]);
  const [listLoading, setListLoading] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const authHeaders = useMemo(
    () => ({ headers: { Authorization: "Bearer " + token } }),
    [token],
  );

  const fetchAccess = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${API}/reimbursements/my-access`,
        authHeaders,
      );
      setAccess({ ...data.data, loaded: true });
    } catch (err) {
      console.error("[fetchAccess] ERROR:", err);
      setAccess((a) => ({ ...a, loaded: true }));
    }
  }, [authHeaders]);

  useEffect(() => {
    fetchAccess();
  }, [fetchAccess]);

  const fetchList = useCallback(async () => {
    if (!access.canChangeStatus) return;
    setListLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/reimbursements/payables?status=${statusTab}`,
        authHeaders,
      );
      setRows(data.data || []);
    } catch (err) {
      console.error("[fetchList] ERROR:", err);
      toastr.error("Failed to load payment queue.");
    } finally {
      setListLoading(false);
    }
  }, [authHeaders, access.canChangeStatus, statusTab]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const subjectOf = (rec) => {
    if (rec?.subject) return rec.subject;
    if (rec?.travel_from || rec?.travel_to) {
      const loc = rec.travel_location ? ` (${rec.travel_location})` : "";
      return `${rec.travel_from || ""} → ${rec.travel_to || ""}${loc}`;
    }
    return "—";
  };

  const statusBadge = (s) => {
    const map = {
      pending: "warning",
      paid: "success",
      "not-paid": "danger",
      hold: "secondary",
    };
    return (
      <span className={`badge bg-${map[s] || "secondary"} text-capitalize`}>
        {s}
      </span>
    );
  };

  const openDetail = useCallback(
    async (id) => {
      setDetail(null);
      setDetailLoading(true);
      setShowModal(true);
      try {
        const { data } = await axios.get(
          `${API}/reimbursements/${id}/detail`,
          authHeaders,
        );
        setDetail(data.data);
      } catch (err) {
        console.error("[openDetail] ERROR:", err);
        toastr.error("Failed to load details.");
        setShowModal(false);
      } finally {
        setDetailLoading(false);
      }
    },
    [authHeaders],
  );

  const changeStatus = useCallback(
    async (id, status) => {
      setGlobalLoading(true);
      try {
        await axios.put(
          `${API}/reimbursements/${id}/status`,
          { status },
          authHeaders,
        );
        toastr.success(`Status updated to ${status}.`);
        fetchList();
        if (detail && detail.record.id === id) {
          setDetail((d) => ({ ...d, record: { ...d.record, status } }));
        }
      } catch (err) {
        console.error("[changeStatus] ERROR:", err);
        toastr.error(
          err?.response?.data?.message || "Failed to update status.",
        );
      } finally {
        setGlobalLoading(false);
      }
    },
    [authHeaders, fetchList, setGlobalLoading],
  );

  const viewFile = async (savename) => {
    if (!savename) return;
    try {
      const response = await fetch(
        `${API}/files/mediaReimbursement/${savename}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      const blob = await response.blob();
      window.open(window.URL.createObjectURL(blob), "_blank");
    } catch (err) {
      console.error("[viewFile] ERROR:", err);
      toastr.error("Failed to open file.");
    }
  };

  const billsTotal = useMemo(() => {
    if (!detail?.files) return 0;
    return detail.files.reduce(
      (s, f) => s + (parseFloat(f.entity_amount) || 0),
      0,
    );
  }, [detail]);

  const columns = useMemo(
    () => [
      { accessorKey: "employee_name", header: "Name", size: 150 },
      {
        accessorKey: "subject",
        header: "Subject",
        size: 190,
        Cell: ({ row }) => subjectOf(row.original),
      },
      { accessorKey: "expense_name", header: "Expense Type", size: 130 },
      {
        accessorKey: "final_amount",
        header: "Amount",
        size: 110,
        Cell: ({ row }) =>
          (row.original.currency_symbol || "") +
          " " +
          (row.original.final_amount || 0),
      },
      { accessorKey: "authorised_by_name", header: "Authorised By", size: 150 },
      {
        accessorKey: "created_date",
        header: "Submit Date",
        size: 115,
        Cell: ({ cell }) => {
          const d = cell.getValue();
          return d ? new Date(d).toLocaleDateString("en-GB") : "—";
        },
      },
      // {
      //   accessorKey: "authorised_date, authorised",
      //   header: "Approval",
      //   size: 115,
      //   Cell: ({ cell }) => {
      //     const d = cell.getValue();

      //     return d
      //       ? new Date(d).toLocaleDateString("en-GB")
      //       : "Pending";
      //   },
      // },
      {
        accessorKey: "status",
        header: "Status",
        size: 100,
        Cell: ({ cell }) => statusBadge(cell.getValue()),
      },
      {
        id: "actions",
        header: "Change Status",
        size: 190,
        enableSorting: false,
        Cell: ({ row }) => {
          const rec = row.original;
          return (
            <div className="d-flex gap-1 align-items-center">
              <select
                className="form-select form-select-sm"
                style={{ minWidth: 110 }}
                value={rec.status}
                onChange={(e) => changeStatus(rec.id, e.target.value)}
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={() => openDetail(rec.id)}
                title="View bills"
              >
                <i className="ti ti-eye" />
              </button>
            </div>
          );
        },
      },
    ],
    // FIX: was [detail]. The cell renderers close over changeStatus -> fetchList
    // -> statusTab. With only [detail], switching tabs then changing a status
    // refetched the PREVIOUS tab. These deps keep the closures current.
    [changeStatus, openDetail],
  );

  if (!access.loaded) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" />
      </div>
    );
  }

  if (!access.canChangeStatus) {
    return (
      <div className="alert alert-warning">
        You do not have access to the reimbursement payment queue.
      </div>
    );
  }

  return (
    <>
      <div className="card">
        <div className="card-header border-0 pb-0">
          <h5 className="mb-1">
            Reimbursement Payments{" "}
            {access.isFinance && <span className="badge bg-info">Finance</span>}
          </h5>
          <p className="text-muted mb-3" style={{ fontSize: "0.85rem" }}>
            Bills were already authorised before submission. Set the status to
            mark them paid.
          </p>
          <ul className="nav nav-tabs nav-tabs-bottom">
            {["pending", "paid", "not-paid", "hold", "all"].map((t) => (
              <li className="nav-item" key={t}>
                <button
                  className={`nav-link text-capitalize ${statusTab === t ? "active" : ""}`}
                  onClick={() => setStatusTab(t)}
                >
                  {t}
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div className="card-body p-0">
          <MaterialReactTable
            columns={columns}
            data={rows}
            state={{ isLoading: listLoading }}
            enableStickyHeader
            muiTableContainerProps={{ sx: { maxHeight: "500px" } }}
            initialState={{ density: "compact" }}
          />
        </div>
      </div>

      {/* Detail modal — view bills/files before paying */}
      {showModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ background: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-xl modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Reimbursement Details</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                />
              </div>

              <div className="modal-body">
                {detailLoading || !detail ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" />
                  </div>
                ) : (
                  <>
                    <div className="row mb-3">
                      <div className="col-md-6">
                        <ul className="list-unstyled mb-0">
                          <li className="mb-2">
                            <strong>Name:</strong> {detail.record.employee_name}
                          </li>
                          <li className="mb-2">
                            <strong>Subject:</strong> {subjectOf(detail.record)}
                          </li>
                          <li className="mb-2">
                            <strong>Submit Date:</strong>{" "}
                            {detail.record.created_date
                              ? new Date(
                                  detail.record.created_date,
                                ).toLocaleDateString("en-GB")
                              : "—"}
                          </li>
                          <li className="mb-2">
                            <strong>Status:</strong>{" "}
                            {statusBadge(detail.record.status)}
                          </li>
                          {detail.record.savename && (
                            <li className="mb-2">
                              <strong>Other File:</strong>{" "}
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-info"
                                onClick={() => viewFile(detail.record.savename)}
                              >
                                <i className="ti ti-paperclip" /> View
                              </button>
                            </li>
                          )}
                        </ul>
                      </div>
                      <div className="col-md-6">
                        <ul className="list-unstyled mb-0">
                          <li className="mb-2">
                            <strong>Expense Type:</strong>{" "}
                            {detail.record.expense_name}
                          </li>
                          <li className="mb-2">
                            <strong>Authorised By:</strong>{" "}
                            {detail.record.authorised_by_name || "—"}
                          </li>
                          <li className="mb-2">
                            <strong>Payment To:</strong>{" "}
                            {detail.record.payment_to || "—"}
                          </li>
                          <li className="mb-2">
                            <strong>Bill Amount:</strong>{" "}
                            {detail.record.entity_currency_name || ""}{" "}
                            {detail.record.final_amount || 0}
                          </li>
                        </ul>
                      </div>
                    </div>

                    <h6 className="fw-semibold">Bills</h6>
                    <div className="table-responsive mb-4">
                      <table className="table table-bordered align-middle">
                        <thead className="table-light">
                          <tr>
                            <th>Bill Date</th>
                            <th>Type</th>
                            <th>Remarks</th>
                            <th>Amount</th>
                            <th>Currency</th>
                            <th>Conv. Rate</th>
                            <th>Conv. Amount</th>
                            <th>File</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detail.files.map((f) => (
                            <tr key={f.id}>
                              <td>
                                {f.bill_date
                                  ? new Date(f.bill_date).toLocaleDateString(
                                      "en-GB",
                                    )
                                  : "—"}
                              </td>
                              <td>{f.reimbursement_type_name || "—"}</td>
                              <td>{f.remarks || "—"}</td>
                              <td>{f.amount}</td>
                              <td>
                                {f.currency_name || f.currency_symbol || "—"}
                              </td>
                              <td>{f.conversion_rate}</td>
                              <td>{f.entity_amount}</td>
                              <td>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-info"
                                  onClick={() => viewFile(f.savename)}
                                >
                                  <i className="ti ti-paperclip" />
                                </button>
                              </td>
                            </tr>
                          ))}
                          {detail.files.length === 0 && (
                            <tr>
                              <td
                                colSpan={8}
                                className="text-center text-muted"
                              >
                                No bills.
                              </td>
                            </tr>
                          )}
                        </tbody>
                        {detail.files.length > 0 && (
                          <tfoot>
                            <tr>
                              <th colSpan={6} className="text-end">
                                Total Conv. Amount
                              </th>
                              <th>{billsTotal.toLocaleString()}</th>
                              <th />
                            </tr>
                          </tfoot>
                        )}
                      </table>
                    </div>

                    <h6 className="fw-semibold">Payment Status</h6>
                    <select
                      className="form-select"
                      style={{ maxWidth: 220 }}
                      value={detail.record.status}
                      onChange={(e) =>
                        changeStatus(detail.record.id, e.target.value)
                      }
                    >
                      {STATUS_OPTIONS.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </>
                )}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
